/**
 * Cycle phase spans — the day ranges behind the thin phase bands drawn above the day
 * squares in the calendar/cycle grids, and behind the Insights phase averages.
 *
 * Everything here is derived from data already recorded (bleeding, first peak day, cycle
 * bounds); nothing is stored. A span whose closing edge isn't known yet is marked `open`
 * so the UI can render it as unfinished instead of implying a phase has ended.
 */

import { findLikelyNewCycleStart, type NFPMethod } from "./cycleLogic";
import { getTodayKey, isCycleOngoing } from "./cycleTiming";
import {
  computeFertileWindow,
  type FertileWindow,
  type FertileWindowObservation,
} from "./fertileWindow";

export type CyclePhaseKey =
  | "menses"
  | "preOvulatory"
  | "fertile"
  | "peak"
  | "postPeak";

export interface PhaseSpan {
  key: CyclePhaseKey;
  /** Inclusive first cycle day of the span. */
  startCd: number;
  /** Inclusive last cycle day known so far. */
  endCd: number;
  /** True when the span's real end isn't determined yet (cycle still running, or no Peak identified). */
  open: boolean;
}

export interface PhaseCycle {
  start_date: string;
  end_date?: string | null;
  first_peak_cd?: number | null;
  /**
   * The real CD 1 was never recorded (partial cycle): day numbers are offsets from the first
   * logged day. Such a cycle has no measurable length for statistics.
   */
  start_inferred?: boolean | null;
}

export type PhaseObservation = FertileWindowObservation;

/**
 * Fixed render order. Bands occupy the same vertical slot for every cycle — a band that
 * moved rows between cycles would read as a different phase.
 */
export const CYCLE_PHASE_ORDER: CyclePhaseKey[] = [
  "menses",
  "preOvulatory",
  "fertile",
  "peak",
  "postPeak",
];

const DAY_MS = 1000 * 60 * 60 * 24;

/** Inclusive cycle length in days, or null when neither bounds nor observations pin it down. */
export const getCycleLength = (
  cycle: PhaseCycle,
  cycleObservations: PhaseObservation[] = []
): number | null => {
  if (cycle.end_date) {
    const start = new Date(cycle.start_date).getTime();
    const end = new Date(cycle.end_date).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return null;
    return Math.floor((end - start) / DAY_MS) + 1;
  }

  // Running cycle: the furthest day observed is as much as we know about it.
  const maxObservedDay = cycleObservations.reduce(
    (max, obs) => Math.max(max, obs.cycle_day),
    0
  );
  return maxObservedDay > 0 ? maxObservedDay : null;
};

/**
 * Menses run: CD 1 forward while consecutive days are logged with bleeding. A missing day
 * ends the run rather than being assumed dry — the band should only claim days we have.
 */
const getMensesSpan = (
  cycleObservations: PhaseObservation[],
  isOngoing: boolean
): PhaseSpan | null => {
  const bleedingByDay = new Map<number, number | null | undefined>();
  for (const obs of cycleObservations) {
    bleedingByDay.set(obs.cycle_day, obs.bleeding);
  }

  let lastBleedingDay = 0;
  for (let cd = 1; ; cd += 1) {
    if (!bleedingByDay.has(cd) || !bleedingByDay.get(cd)) break;
    lastBleedingDay = cd;
  }

  if (lastBleedingDay === 0) return null;

  return {
    key: "menses",
    startCd: 1,
    endCd: lastBleedingDay,
    // Only open if bleeding is still the latest thing logged in a running cycle.
    open: isOngoing && lastBleedingDay === Math.max(...bleedingByDay.keys()),
  };
};

export interface PhaseSpanOptions {
  /** Methods whose fertile-window rules apply. Defaults to Marquette, as elsewhere. */
  methods?: NFPMethod[];
  /** Shortest completed cycle on record, for the shortest − 20 calculation. */
  shortestCycleLength?: number | null;
  /** Completed cycles on record; the shortest − 20 rule needs six. */
  completedCycleCount?: number;
  isPostpartum?: boolean;
  /** Today's date key; a cycle whose end hasn't been reached yet is still running. */
  today?: string;
}

/**
 * Phase spans for one cycle, in `CYCLE_PHASE_ORDER`. Spans are clamped to the days the
 * cycle actually has, so nothing is drawn past the end of a completed cycle.
 *
 * The fertile window comes from the selected methods' own rules (see fertileWindow.ts) —
 * mucus onset, monitor readings, Peak Day, thermal confirmation — not from a calendar guess.
 */
export const getCyclePhases = (
  cycle: PhaseCycle,
  cycleObservations: PhaseObservation[] = [],
  options: PhaseSpanOptions = {}
): {
  spans: PhaseSpan[];
  window: FertileWindow;
  /**
   * Cycle day of bleeding after the window closed that looks like the next period (see
   * findLikelyNewCycleStart) — the cycle start was probably never marked. Days from here on
   * aren't painted as any phase: not infertile, and not a fertile-window claim either.
   */
  likelyNewCycleCd: number | null;
} => {
  const cycleLength = getCycleLength(cycle, cycleObservations);
  if (!cycleLength) {
    return {
      spans: [],
      likelyNewCycleCd: null,
      window: computeFertileWindow(cycleObservations, options.methods, {
        peakCd: cycle.first_peak_cd,
        shortestCycleLength: options.shortestCycleLength,
        completedCycleCount: options.completedCycleCount,
        isPostpartum: options.isPostpartum,
        startInferred: Boolean(cycle.start_inferred),
      }),
    };
  }

  // A future end_date (a pre-logged next cycle start) doesn't make the cycle finished.
  const isOngoing = isCycleOngoing(cycle, options.today ?? getTodayKey());
  const peakCd = cycle.first_peak_cd ?? null;
  const window = computeFertileWindow(cycleObservations, options.methods, {
    peakCd,
    shortestCycleLength: options.shortestCycleLength,
    completedCycleCount: options.completedCycleCount,
    isPostpartum: options.isPostpartum,
    startInferred: Boolean(cycle.start_inferred),
  });
  const spans: PhaseSpan[] = [];

  const menses = getMensesSpan(cycleObservations, isOngoing);
  if (menses) spans.push(menses);

  // Pre-ovulatory picks up where menses leaves off rather than starting at CD 1 — a CD 1
  // start would just redraw the menses band one row down. Omitted when the window opens as
  // soon as bleeding ends, since then there are no infertile pre-ovulatory days to mark.
  const preOvulatoryStart = (menses?.endCd ?? 0) + 1;
  const preOvulatoryEnd = Math.min(window.startCd - 1, cycleLength);
  if (preOvulatoryEnd >= preOvulatoryStart) {
    spans.push({
      key: "preOvulatory",
      startCd: preOvulatoryStart,
      endCd: preOvulatoryEnd,
      open: false,
    });
  }

  // A window with no closing sign yet is genuinely open in a running cycle, but unknowable in
  // a completed one — painting it over every remaining day would state more than the data does.
  const fertileEndCd = window.endCd;
  if (window.startCd <= cycleLength && (fertileEndCd || isOngoing)) {
    const resolvedEnd = fertileEndCd ?? cycleLength;
    spans.push({
      key: "fertile",
      startCd: window.startCd,
      endCd: Math.min(resolvedEnd, cycleLength),
      open: !fertileEndCd || (isOngoing && resolvedEnd > cycleLength),
    });
  }

  if (peakCd && peakCd <= cycleLength) {
    spans.push({ key: "peak", startCd: peakCd, endCd: peakCd, open: false });
  }

  // Post-peak starts where the fertile window closes, not at Peak + 1: the three days after
  // Peak are still inside the window, and a band that overlapped them would contradict it.
  // It ends before a later period: if a cycle start went unmarked, the next cycle's bleeding
  // and fertile window would otherwise be shown as this cycle's infertile phase.
  const postPeakStart = (fertileEndCd ?? 0) + 1;
  const likelyNewCycleCd = fertileEndCd
    ? findLikelyNewCycleStart(cycleObservations, fertileEndCd)
    : null;
  const postPeakEnd = likelyNewCycleCd ? Math.min(likelyNewCycleCd - 1, cycleLength) : cycleLength;
  if (fertileEndCd && postPeakStart <= postPeakEnd) {
    spans.push({
      key: "postPeak",
      startCd: postPeakStart,
      endCd: postPeakEnd,
      open: isOngoing && !likelyNewCycleCd,
    });
  }

  return {
    likelyNewCycleCd,
    spans: spans.sort(
      (a, b) => CYCLE_PHASE_ORDER.indexOf(a.key) - CYCLE_PHASE_ORDER.indexOf(b.key)
    ),
    window,
  };
};

/** Spans alone, for callers that don't need the window's derivation basis. */
export const getCyclePhaseSpans = (
  cycle: PhaseCycle,
  cycleObservations: PhaseObservation[] = [],
  options: PhaseSpanOptions = {}
): PhaseSpan[] => getCyclePhases(cycle, cycleObservations, options).spans;

/** Phases covering a single cycle day, in render order. */
export const getPhasesForCycleDay = (
  cycleDay: number,
  spans: PhaseSpan[]
): PhaseSpan[] =>
  spans.filter((span) => cycleDay >= span.startCd && cycleDay <= span.endCd);

/**
 * True when the day is in the post-Peak phase — after the method's fertile window closed and
 * before any later period. The only safe basis for greying mucus as "not interpreted as
 * fertile": unlike Peak + 3, it honours the Sympto-Thermal double check, a restarted count,
 * and an unmarked next cycle.
 */
export const isPostPeakPhaseDay = (cycleDay: number, spans: readonly PhaseSpan[]): boolean =>
  spans.some((span) => span.key === "postPeak" && cycleDay >= span.startCd && cycleDay <= span.endCd);
